import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import {
  GuestJourneyBenchmarkMetric,
  GuestJourneyBenchmarkSection,
} from 'api/reports-service/guest-journey-reports.types';
import MovementIndicator from 'components/movement-indicator/movement-indicator.component';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BenchmarksPerfBar from './benchmarks-perf-bar.component';
import BenchmarksSectionGrid from './benchmarks-section-grid.component';

export type BenchmarksMetricsGridProps = {
  sections?: GuestJourneyBenchmarkSection[];
  loading?: boolean;
};

const buildColumns = (
  t: TFunction,
): GridColDef<GuestJourneyBenchmarkMetric>[] => [
  {
    // Metric name + description are BE-resolved/display-ready; rendered verbatim.
    field: 'metric',
    headerName: t('page.guestJourney.benchmarks.columns.metric'),
    flex: 2.2,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => (
      <Stack
        spacing={0.25}
        paddingY={1}
        data-testid={`Benchmarks.Metric.${row.metricName}`}
      >
        <Typography variant="body2">{row.metricName}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.metricDescription}
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'brand',
    headerName: t('page.guestJourney.benchmarks.columns.brand'),
    flex: 1,
    minWidth: 100,
    sortable: false,
    display: 'flex',
    renderCell: ({ row }) => (
      <Typography
        variant="body2"
        data-testid={`Benchmarks.Metric.${row.metricName}.Brand`}
      >
        {row.brandValue}
      </Typography>
    ),
  },
  {
    field: 'benchmark',
    headerName: t('page.guestJourney.benchmarks.columns.benchmark'),
    flex: 1,
    minWidth: 100,
    sortable: false,
    display: 'flex',
    renderCell: ({ row }) => (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid={`Benchmarks.Metric.${row.metricName}.Benchmark`}
      >
        {row.benchmarkValue}
      </Typography>
    ),
  },
  {
    field: 'vsBaseline',
    headerName: t('page.guestJourney.benchmarks.columns.vsBaseline'),
    flex: 1.6,
    minWidth: 140,
    sortable: false,
    display: 'flex',
    renderHeader: () => (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {t('page.guestJourney.benchmarks.columns.vsBaseline')}
        </Typography>
        <Tooltip title={t('page.guestJourney.benchmarks.vsBaselineTooltip')}>
          <InfoOutlined
            fontSize="inherit"
            color="action"
            data-testid="Benchmarks.VsBaseline.Info"
          />
        </Tooltip>
      </Stack>
    ),
    renderCell: ({ row }) => (
      <MovementIndicator
        movement={row.vsDelta}
        size="small"
        data-testid={`Benchmarks.Metric.${row.metricName}.VsBaseline`}
      />
    ),
  },
  {
    field: 'performance',
    headerName: t('page.guestJourney.benchmarks.columns.performance'),
    flex: 2,
    minWidth: 140,
    sortable: false,
    display: 'flex',
    renderCell: ({ row }) => (
      <Box width="100%">
        <BenchmarksPerfBar
          performance={row.performancePct}
          baselineTick={row.benchmarkMarkerPct}
          // Below the cuisine baseline → the bar reads red + surfaces the hint.
          belowBaseline={row.performancePct < row.benchmarkMarkerPct}
          data-testid={`Benchmarks.Metric.${row.metricName}.PerfBar`}
        />
      </Box>
    ),
  },
];

const LegendSwatch = ({ color, label }: { color: string; label: string }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Box width={12} height={12} borderRadius={0.5} bgcolor={color} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

const BenchmarksMetricsGrid = ({
  sections = [],
  loading,
}: BenchmarksMetricsGridProps) => {
  const { t } = useTranslation();

  // Build once and share across every section grid so the stacked grids stay
  // column-aligned.
  const columns = useMemo(() => buildColumns(t), [t]);

  return (
    <Stack spacing={2} data-testid="Benchmarks.MetricsGrid">
      {sections.map(section => (
        <BenchmarksSectionGrid
          key={section.section}
          title={section.section}
          columns={columns}
          metrics={section.rows}
          loading={loading}
          data-testid={`Benchmarks.Section.${section.section}`}
        />
      ))}

      <Stack
        direction="row"
        flexWrap="wrap"
        spacing={2}
        data-testid="Benchmarks.Legend"
      >
        <LegendSwatch
          color="success.main"
          label={t('page.guestJourney.benchmarks.legend.above')}
        />
        <LegendSwatch
          color="error.main"
          label={t('page.guestJourney.benchmarks.legend.below')}
        />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box width="2px" height={12} bgcolor="text.primary" />
          <Typography variant="caption" color="text.secondary">
            {t('page.guestJourney.benchmarks.legend.baseline')}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default BenchmarksMetricsGrid;
